import { useQuery } from '@tanstack/react-query';
import { GitCommitHorizontal, GitPullRequest, GitBranch } from 'lucide-react';
import type { TicketFix } from '@vd/types';
import { api } from '../../lib/api';
import styles from './TicketFixes.module.css';

/** An http or https address, or null: only these become links (TOADO-844). */
export function webLink(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Where a commit is shown in the project's repository, when the project names
 * one: the repository's path and `/commit/<hash>`, with any query or fragment
 * left off, so a commit never lands in a `#readme` (Codex review round 1).
 */
export function commitLink(repositoryUrl: string | null, commit: string): string | null {
  const repository = webLink(repositoryUrl);
  if (!repository || !/^[0-9a-f]{7,64}$/.test(commit)) return null;
  const url = new URL(repository);
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/commit/${commit}`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

/** "PR #42" for a GitHub or Bitbucket pull request, "MR !42" for a GitLab merge request, else "Pull request". */
export function pullRequestLabel(address: string): string {
  const github = /\/pull(?:-requests)?\/(\d+)(?:[/?#]|$)/.exec(address);
  if (github) return `PR #${github[1]}`;
  const gitlab = /\/merge_requests\/(\d+)(?:[/?#]|$)/.exec(address);
  return gitlab ? `MR !${gitlab[1]}` : 'Pull request';
}

/**
 * What fixed this ticket, as recorded (TOADO-844, MCP `record_fix`): each fix
 * on one line, "Fixed in abc1234 (PR #42)", with who recorded it and, when an
 * agent did, which. Nothing when there is none. The commit links into the
 * project's repository when it names one; every value is shown as text.
 */
export function TicketFixes({ ticketId }: { ticketId: string }) {
  // Under the ticket's own key, so every live event that refreshes the ticket refreshes this too.
  const { data } = useQuery({ queryKey: ['ticket', ticketId, 'fixes'], queryFn: () => api.listTicketFixes(ticketId) });
  if (!data || data.fixes.length === 0) return null;
  return (
    <ul className={styles.fixes} aria-label="Fixes" data-testid="ticket-fixes">
      {data.fixes.map((fix) => <FixLine key={fix.id} fix={fix} repositoryUrl={data.repositoryUrl} />)}
    </ul>
  );
}

function FixLine({ fix, repositoryUrl }: { fix: TicketFix; repositoryUrl: string | null }) {
  const commitHref = fix.commit ? commitLink(repositoryUrl, fix.commit) : null;
  const pullRequestHref = webLink(fix.pullRequestUrl);
  const by = fix.viaAgent ? `${fix.recordedBy} via ${fix.viaAgent}` : fix.recordedBy;
  return (
    <li className={styles.fix} data-testid="ticket-fix">
      <span className={styles.label}>Fixed in</span>
      {fix.commit && (
        <span className={styles.part}>
          <GitCommitHorizontal size={12} aria-hidden />
          {commitHref
            ? <a href={commitHref} target="_blank" rel="noopener noreferrer" className={styles.code}>{fix.commit.slice(0, 7)}</a>
            : <span className={styles.code} title={fix.commit}>{fix.commit.slice(0, 7)}</span>}
        </span>
      )}
      {fix.pullRequestUrl && (
        <span className={styles.part}>
          <GitPullRequest size={12} aria-hidden />
          {pullRequestHref
            ? <a href={pullRequestHref} target="_blank" rel="noopener noreferrer">{pullRequestLabel(pullRequestHref)}</a>
            : <span>{fix.pullRequestUrl}</span>}
        </span>
      )}
      {fix.branch && (
        <span className={styles.part}>
          <GitBranch size={12} aria-hidden />
          <span className={styles.code}>{fix.branch}</span>
        </span>
      )}
      {fix.summary && <span className={styles.summary}>{fix.summary}</span>}
      <span className={styles.by}>{by}</span>
    </li>
  );
}
